// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import { horizontalBars } from '../components/charts';
import { bySector, rankedBy } from '../analysis';
import { OVER } from '../sectors';
import { escapeHtml, formatPercent } from '../utils/format';
import { glossaryTerm } from '../glossary';

const mt = (t: number) => `${(t / 1e6).toFixed(1)} Mt`;
const CO2 = '#6b7280';
const CH4 = OVER;
const N2O = '#8b5cf6';
const OTHER = '#cbd5e1';

export function renderGases(root: HTMLElement, ctx: ViewContext): void {
  const { facilities, meta } = ctx.data;
  const n = meta.national;

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">It isn't all carbon dioxide</h1>
      <p class="view-sub">
        ${formatPercent(n.methaneShare)} of these emissions — ${mt(n.ch4)} — is ${glossaryTerm('methane', 'methane')},
        mostly ${glossaryTerm('fugitive', 'leaking')} from coal mines and gas operations. Because methane traps far more
        heat than carbon dioxide tonne for tonne, where it dominates it dominates the climate impact. Everything here is
        already in CO₂-equivalent, so the gases can be compared directly.
      </p>
    </div>

    <div class="stat-grid">
      <div class="stat"><div class="stat-label">Carbon dioxide</div><div class="stat-value slate">${mt(n.co2)}</div><div class="stat-note">${formatPercent((n.co2 / n.totalCovered) * 100)} of covered</div></div>
      <div class="stat"><div class="stat-label">Methane</div><div class="stat-value over">${mt(n.ch4)}</div><div class="stat-note">${formatPercent(n.methaneShare)} of covered</div></div>
      <div class="stat"><div class="stat-label">Nitrous oxide</div><div class="stat-value slate">${mt(n.n2o)}</div><div class="stat-note">${formatPercent((n.n2o / n.totalCovered) * 100)} of covered</div></div>
      <div class="stat"><div class="stat-label">Other gases</div><div class="stat-value slate">${mt(n.ghgOther)}</div><div class="stat-note">${formatPercent((n.ghgOther / n.totalCovered) * 100)} of covered</div></div>
    </div>

    <div class="panel">
      <div class="panel-title">Gas mix by industry</div>
      <div class="panel-sub">Each bar is 100% of an industry's covered emissions, split into carbon dioxide, methane and nitrous oxide. Coal mining and gas are dominated by methane; cement and steel by carbon dioxide.</div>
      <div class="legend" style="margin-bottom:var(--space-md)">
        <span class="legend-item" style="cursor:default"><span class="legend-swatch" style="background:${CO2}"></span>Carbon dioxide</span>
        <span class="legend-item" style="cursor:default"><span class="legend-swatch" style="background:${CH4}"></span>Methane</span>
        <span class="legend-item" style="cursor:default"><span class="legend-swatch" style="background:${N2O}"></span>Nitrous oxide</span>
        <span class="legend-item" style="cursor:default"><span class="legend-swatch" style="background:${OTHER}"></span>Other</span>
      </div>
      <div data-role="sectormix"></div>
    </div>

    <div class="panel" style="margin-top:var(--space-lg)">
      <div class="panel-title">The most methane-intensive large facilities</div>
      <div class="panel-sub">Facilities over 200,000 tCO₂-e ranked by the share of their emissions that is methane. Underground coal mines top the list — the gas vents straight from the coal seam. Click to open.</div>
      <div class="chart-scroll" data-role="methane"></div>
    </div>
  `;

  // Gas mix by sector — 100% stacked HTML bars.
  const sectors = bySector(facilities).filter((s) => s.covered > 0).slice(0, 16);
  const mixEl = root.querySelector('[data-role="sectormix"]') as HTMLElement;
  mixEl.innerHTML = sectors
    .map((s) => {
      const segs = [
        { c: CO2, v: s.co2, l: 'Carbon dioxide' },
        { c: CH4, v: s.ch4, l: 'Methane' },
        { c: N2O, v: s.n2o, l: 'Nitrous oxide' },
        { c: OTHER, v: Math.max(0, s.covered - s.co2 - s.ch4 - s.n2o), l: 'Other' },
      ].filter((x) => x.v > 0);
      const bar = segs
        .map((x) => `<div class="mix-seg" style="flex:${x.v};background:${x.c}" data-tip="${escapeHtml(`${s.label}\n${x.l}: ${mt(x.v)} (${formatPercent((x.v / s.covered) * 100)})`)}"></div>`)
        .join('');
      return `<div style="display:flex;align-items:center;gap:var(--space-md);margin-bottom:8px">
        <div style="width:230px;flex-shrink:0;font-size:var(--font-size-sm);overflow:hidden;text-overflow:ellipsis;white-space:nowrap" data-tip="${escapeHtml(s.label)}">${escapeHtml(s.label)}</div>
        <div class="mix-bar" style="flex:1;min-width:0">${bar}</div>
        <div style="width:64px;flex-shrink:0;text-align:right;font-family:var(--font-mono);font-size:var(--font-size-xs);color:${CH4}">${formatPercent(s.methaneShare ?? 0, 0)} CH₄</div>
      </div>`;
    })
    .join('');

  // Methane-share leaderboard.
  const methane = rankedBy(facilities.filter((f) => f.covered > 200_000), 'methaneShare').slice(0, 14);
  (root.querySelector('[data-role="methane"]') as HTMLElement).appendChild(
    horizontalBars(
      methane.map((f) => ({
        id: f.slug,
        label: f.name.length > 26 ? f.name.slice(0, 25) + '…' : f.name,
        value: f.methaneShare ?? 0,
        colour: CH4,
        tip: `${f.name}\n${f.company}\n${formatPercent(f.methaneShare ?? 0)} methane · ${mt(f.ch4)} CH₄ of ${mt(f.covered)} total`,
      })),
      { width: 720, labelWidth: 210, valueFormat: (v) => `${v.toFixed(0)}%`, onClick: (id) => ctx.openFacility(id) },
    ),
  );
}
